import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectApiConnect } from '../lib/api/projectApi.connect';
import { proposalProjectApiConnect } from '../lib/api/proposalProjectApi.connect';
import { projectDataClient } from '../lib/connect/clients';
/**
 * Fetch list of projects with optional filtering and pagination
 */ export function useProjects(params) {
    return useQuery({
        queryKey: [
            'projects',
            params?.status,
            params?.page,
            params?.limit
        ],
        queryFn: ()=>projectApiConnect.listProjects(params),
        staleTime: 30000
    });
}
/**
 * Fetch a single project by ID
 */ export function useProject(projectId) {
    return useQuery({
        queryKey: [
            'project',
            projectId
        ],
        queryFn: ()=>{
            if (!projectId) {
                throw new Error('Project ID is required');
            }
            return projectApiConnect.getProject(projectId);
        },
        enabled: !!projectId,
        staleTime: 30000
    });
}
/**
 * Create a new project
 */ export function useCreateProject() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data)=>{
            // Create the project first (JSON only), then upload files separately.
            // projectApiConnect.createProject rejects a request that still carries
            // files, so stripping them here is load-bearing rather than tidiness.
            const { files, ...createData } = data;
            const response = await projectApiConnect.createProject(createData);
            if (files && files.length > 0 && response.project?.projectId) {
                const projectId = response.project.projectId;
                const uploadResult = await projectApiConnect.uploadProjectFiles(projectId, files);
                // Trigger processing and store batchId so the project page shows progress
                const attachmentIds = uploadResult.files.map((f)=>f.attachmentId).filter(Boolean);
                if (attachmentIds.length > 0) {
                    const batch = await proposalProjectApiConnect.processBatch(projectId, attachmentIds);
                    try {
                        sessionStorage.setItem(`batch:${projectId}`, batch.batchId);
                    } catch  {
                    // sessionStorage may not be available
                    }
                }
                return {
                    ...response,
                    filesUploaded: uploadResult.files.length
                };
            }
            return response;
        },
        onSuccess: ()=>{
            // Invalidate projects list to refetch
            queryClient.invalidateQueries({
                queryKey: [
                    'projects'
                ]
            });
        }
    });
}
/**
 * Update a project's name and/or description
 */ export function useUpdateProject() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ projectId, ...data })=>projectApiConnect.updateProject(projectId, data),
        onSuccess: (_data, { projectId })=>{
            // Invalidate both the projects list and the specific project
            queryClient.invalidateQueries({
                queryKey: [
                    'projects'
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'project',
                    projectId
                ]
            });
        }
    });
}
// invalidateAllProjectViews fans an invalidation across every per-project
// query key in the app. Without this, archive/unarchive/delete from one
// surface (e.g. the projects list) leaves stale data in the proposal or
// contract dashboards (which use their own query keys).
function invalidateAllProjectViews(queryClient, projectId) {
    queryClient.invalidateQueries({
        queryKey: [
            'projects'
        ]
    });
    queryClient.invalidateQueries({
        queryKey: [
            'project',
            projectId
        ]
    });
    queryClient.invalidateQueries({
        queryKey: [
            'proposal-project',
            projectId
        ]
    });
    queryClient.invalidateQueries({
        queryKey: [
            'contract-project',
            projectId
        ]
    });
}
function evictAllProjectViews(queryClient, projectId) {
    queryClient.invalidateQueries({
        queryKey: [
            'projects'
        ]
    });
    queryClient.removeQueries({
        queryKey: [
            'project',
            projectId
        ]
    });
    queryClient.removeQueries({
        queryKey: [
            'proposal-project',
            projectId
        ]
    });
    queryClient.removeQueries({
        queryKey: [
            'contract-project',
            projectId
        ]
    });
}
/**
 * Archive a project (owner only)
 */ export function useArchiveProject() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (projectId)=>projectApiConnect.archiveProject(projectId),
        onSuccess: (_data, projectId)=>{
            invalidateAllProjectViews(queryClient, projectId);
        }
    });
}
/**
 * Unarchive a project (owner only)
 */ export function useUnarchiveProject() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (projectId)=>projectApiConnect.unarchiveProject(projectId),
        onSuccess: (_data, projectId)=>{
            invalidateAllProjectViews(queryClient, projectId);
        }
    });
}
/**
 * Permanently delete a project (owner only).
 * WARNING: This is irreversible - all project data will be permanently lost.
 */ export function useDeleteProject() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (projectId)=>projectApiConnect.deleteProject(projectId),
        onSuccess: (_data, projectId)=>{
            evictAllProjectViews(queryClient, projectId);
        }
    });
}
/**
 * Add members to a project (owner only)
 */ export function useAddMembers() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ projectId, ...data })=>projectApiConnect.addMembers(projectId, data),
        onSuccess: (_data, { projectId })=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'project',
                    projectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'projects'
                ]
            });
        }
    });
}
/**
 * Remove a member from a project (owner only)
 */ export function useRemoveMember() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ projectId, userId })=>projectApiConnect.removeMember(projectId, userId),
        onSuccess: (_data, { projectId })=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'project',
                    projectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'projects'
                ]
            });
        }
    });
}
/**
 * Update a member's role (owner only)
 */ export function useUpdateMemberRole() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ projectId, userId, ...data })=>projectApiConnect.updateMemberRole(projectId, userId, data),
        onSuccess: (_data, { projectId })=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'project',
                    projectId
                ]
            });
        }
    });
}
/**
 * Transfer project ownership (owner only)
 */ export function useTransferOwnership() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ projectId, ...data })=>projectApiConnect.transferOwnership(projectId, data),
        onSuccess: (_data, { projectId })=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'project',
                    projectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'projects'
                ]
            });
        }
    });
}
/**
 * Leave a project (member/view_only only)
 */ export function useLeaveProject() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (projectId)=>projectApiConnect.leaveProject(projectId),
        onSuccess: (_data, projectId)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'project',
                    projectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'projects'
                ]
            });
        }
    });
}
/**
 * Convert a proposal project to a contract project
 */ export function useConvertToContract() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ projectId, ...data })=>projectApiConnect.convertToContract(projectId, data),
        onSuccess: (_data, { projectId })=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'projects'
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'project',
                    projectId
                ]
            });
        }
    });
}
/**
 * Phase 0b: Promote a submitted proposal to a Pre-Award workspace. Freezes
 * the bid baseline in pangaea, creates the PreAwardProject row + a sibling
 * zou Project (type=pre_award), and seeds the 8 SDS §3.3 readiness rows.
 * Returns the new pre_award_project_id so the caller can route to
 * /delivery/pre-award/:id.
 */ export function useConvertToPreAward() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ proposalProjectId })=>{
            const response = await projectDataClient.convertProposalToPreAward({
                proposalProjectId
            });
            if (!response.summary) {
                throw new Error('ConvertProposalToPreAward returned no summary');
            }
            return response.summary;
        },
        onSuccess: (_summary, { proposalProjectId })=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'projects'
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'project',
                    proposalProjectId
                ]
            });
        }
    });
}
